import { useState, useEffect } from "react";
import { ArrowRight, Shield, TrendingUp, TrendingDown, Lock, RefreshCw, Clock, CheckCircle, Phone, Star, ChevronRight, Info, Newspaper, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const PHONE_NUMBER = "+17787590400";

// Crypto icon URLs for the top cards
const cryptoIcons = {
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.png",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
  SOL: "https://cryptologos.cc/logos/solana-sol-logo.png",
  ADA: "https://cryptologos.cc/logos/cardano-ada-logo.png",
};

// Featured cryptos for the top cards
const FEATURED_CRYPTOS = ["bitcoin", "ethereum", "solana", "cardano"];

// Mini sparkline chart component - responsive
const Sparkline = ({ data, isPositive, width = 100, height = 40 }) => {
  if (!data || data.length === 0) return <div style={{ width, height }} />;
  
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  
  const strokeColor = isPositive ? "#3B82F6" : "#EF4444";
  
  return (
    <svg width={width} height={height} className="overflow-visible flex-shrink-0">
      <polyline
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};

// Featured crypto card component (for top section)
const FeaturedCryptoCard = ({ crypto }) => {
  const isPositive = crypto.change_24h >= 0;
  
  const formatPrice = (price) => {
    if (price >= 1000) {
      return `C$${price.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    } else if (price >= 1) {
      return `C$${price.toFixed(2)}`;
    } else {
      return `C$${price.toFixed(2)}`;
    }
  };

  return (
    <Link 
      to={`/crypto/${crypto.id}`}
      className="bg-white rounded-xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#001435] flex flex-col h-full"
      data-testid={`featured-crypto-card-${crypto.symbol}`}
    >
      <div className="flex items-start justify-between mb-3">
        <img
          src={cryptoIcons[crypto.symbol] || crypto.image}
          alt={crypto.name}
          className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
          onError={(e) => { e.target.src = crypto.image; }}
        />
        <div className={`flex items-center gap-1 ${isPositive ? 'text-[#3B82F6]' : 'text-red-500'}`}>
          {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span className="text-xs sm:text-sm font-medium">
            {isPositive ? '+' : ''}{crypto.change_24h?.toFixed(1)}%
          </span>
        </div>
      </div>
      
      <h3 className="text-sm sm:text-base font-medium text-gray-800 mb-1">{crypto.name}</h3>
      <p className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
        {formatPrice(crypto.price_cad)}
      </p>
      
      <div className="mt-auto">
        <Sparkline data={crypto.sparkline} isPositive={isPositive} width={100} height={35} />
      </div>
    </Link>
  );
};

// Loading skeleton for featured cards
const FeaturedCardSkeleton = () => (
  <div className="bg-white rounded-xl p-4 sm:p-5 shadow-lg border-l-4 border-gray-200 animate-pulse">
    <div className="flex justify-between mb-3">
      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
      <div className="w-12 h-4 bg-gray-200 rounded"></div>
    </div>
    <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
    <div className="h-6 w-24 bg-gray-200 rounded mb-3"></div>
    <div className="h-8 w-full bg-gray-100 rounded"></div>
  </div>
);

// Format price with appropriate decimals
const formatPrice = (price) => {
  if (price >= 1000) {
    return `$${price.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } else if (price >= 1) {
    return `$${price.toFixed(2)}`;
  } else if (price >= 0.01) {
    return `$${price.toFixed(4)}`;
  } else {
    return `$${price.toFixed(6)}`;
  }
};

// Format large numbers (volume, market cap)
const formatLargeNumber = (num) => {
  if (num >= 1e12) {
    return `$${(num / 1e12).toFixed(2)}T`;
  } else if (num >= 1e9) {
    return `$${(num / 1e9).toFixed(2)}B`;
  } else if (num >= 1e6) {
    return `$${(num / 1e6).toFixed(2)}M`;
  } else {
    return `$${num?.toLocaleString() || 0}`;
  }
};

// Compact number for mobile
const formatCompact = (num) => {
  if (num >= 1e12) return `${(num / 1e12).toFixed(1)}T`;
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  return num?.toLocaleString() || '0';
};

// Percentage change component with color
const PercentChange = ({ value, size = "sm" }) => {
  if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
  
  const isPositive = value >= 0;
  const colorClass = isPositive ? "text-[#3B82F6]" : "text-red-500";
  const textSize = size === "xs" ? "text-xs" : "text-sm";
  
  return (
    <span className={`flex items-center gap-0.5 ${colorClass} font-medium ${textSize}`}>
      {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {isPositive ? "+" : ""}{value.toFixed(1)}%
    </span>
  );
};

// Mobile Crypto Card (replaces table on mobile)
const MobileCryptoCard = ({ crypto }) => {
  const isPositive = crypto.change_7d >= 0;
  
  return (
    <Link 
      to={`/crypto/${crypto.id}`}
      className="block bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
      data-testid={`crypto-card-mobile-${crypto.symbol}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <img 
            src={crypto.image} 
            alt={crypto.name}
            className="w-8 h-8 rounded-full"
          />
          <div>
            <span className="font-semibold text-gray-900 text-sm">{crypto.name}</span>
            <span className="text-gray-400 text-xs ml-1.5">{crypto.symbol}</span>
          </div>
        </div>
        <Sparkline data={crypto.sparkline} isPositive={isPositive} width={60} height={24} />
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bold text-gray-900">{formatPrice(crypto.price_cad)}</p>
          <p className="text-xs text-gray-500">MCap: {formatCompact(crypto.market_cap)}</p>
        </div>
        <div className="text-right">
          <PercentChange value={crypto.change_24h} size="xs" />
          <p className="text-xs text-gray-400 mt-0.5">24h</p>
        </div>
      </div>
    </Link>
  );
};

// Desktop Table Row
const CryptoTableRow = ({ crypto }) => {
  return (
    <tr 
      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
      data-testid={`crypto-row-${crypto.symbol}`}
    >
      <td className="py-3 px-2">
        <Link to={`/crypto/${crypto.id}`} className="flex items-center gap-2">
          <img 
            src={crypto.image} 
            alt={crypto.name}
            className="w-6 h-6 rounded-full"
          />
          <span className="font-semibold text-gray-900 text-sm hover:text-blue-600">{crypto.name}</span>
          <span className="text-gray-400 text-xs">{crypto.symbol}</span>
        </Link>
      </td>
      <td className="py-3 px-2 font-semibold text-gray-900 text-sm">
        <Link to={`/crypto/${crypto.id}`}>{formatPrice(crypto.price_cad)}</Link>
      </td>
      <td className="py-3 px-2 text-sm">
        <Link to={`/crypto/${crypto.id}`}><PercentChange value={crypto.change_1h} /></Link>
      </td>
      <td className="py-3 px-2 text-sm">
        <Link to={`/crypto/${crypto.id}`}><PercentChange value={crypto.change_24h} /></Link>
      </td>
      <td className="py-3 px-2 text-sm">
        <Link to={`/crypto/${crypto.id}`}><PercentChange value={crypto.change_7d} /></Link>
      </td>
      <td className="py-3 px-2 text-xs text-gray-600">
        <Link to={`/crypto/${crypto.id}`}>{formatLargeNumber(crypto.volume_24h)}</Link>
      </td>
      <td className="py-3 px-2 text-xs text-gray-600">
        <Link to={`/crypto/${crypto.id}`}>{formatLargeNumber(crypto.market_cap)}</Link>
      </td>
      <td className="py-3 px-2">
        <Link to={`/crypto/${crypto.id}`}>
          <Sparkline data={crypto.sparkline} isPositive={crypto.change_7d >= 0} width={80} height={30} />
        </Link>
      </td>
    </tr>
  );
};

// Loading skeletons
const MobileCardSkeleton = () => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 animate-pulse">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-5 h-4 bg-gray-200 rounded"></div>
        <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
        <div className="w-20 h-4 bg-gray-200 rounded"></div>
      </div>
      <div className="w-14 h-6 bg-gray-200 rounded"></div>
    </div>
    <div className="flex items-center justify-between">
      <div className="w-20 h-5 bg-gray-200 rounded"></div>
      <div className="w-12 h-4 bg-gray-200 rounded"></div>
    </div>
  </div>
);

const TableRowSkeleton = () => (
  <tr className="border-b border-gray-100 animate-pulse">
    <td className="py-3 px-2"><div className="w-4 h-4 bg-gray-200 rounded"></div></td>
    <td className="py-3 px-2">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
        <div className="w-16 h-4 bg-gray-200 rounded"></div>
      </div>
    </td>
    <td className="py-3 px-2"><div className="w-16 h-4 bg-gray-200 rounded"></div></td>
    <td className="py-3 px-2"><div className="w-10 h-4 bg-gray-200 rounded"></div></td>
    <td className="py-3 px-2"><div className="w-10 h-4 bg-gray-200 rounded"></div></td>
    <td className="py-3 px-2"><div className="w-10 h-4 bg-gray-200 rounded"></div></td>
    <td className="py-3 px-2"><div className="w-14 h-4 bg-gray-200 rounded"></div></td>
    <td className="py-3 px-2"><div className="w-16 h-4 bg-gray-200 rounded"></div></td>
    <td className="py-3 px-2"><div className="w-20 h-7 bg-gray-200 rounded"></div></td>
  </tr>
);

export default function Crypto() {
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingNews, setLoadingNews] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchCryptoPrices();
    fetchNews();
    const interval = setInterval(fetchCryptoPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchCryptoPrices = async () => {
    try {
      const response = await axios.get(`${API}/api/crypto/prices`);
      setCryptoPrices(response.data.prices || []);
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      console.error("Error fetching crypto prices:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/api/crypto/news?limit=4`);
      setNews(response.data.news?.slice(0, 4) || []);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoadingNews(false);
    }
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return "";
    const date = new Date(lastUpdated);
    return date.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
  };

  const featuredCryptos = cryptoPrices.filter(c => FEATURED_CRYPTOS.includes(c.id));

  return (
    <div className="min-h-screen bg-[#001435]" data-testid="crypto-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-8 sm:pt-12 pb-8 sm:pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 sm:mb-6">
              Buy & Sell{" "}
              <span className="text-[#3B82F6]">Cryptocurrency</span>
            </h1>
            <p className="text-blue-200/70 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto mb-6 sm:mb-8 px-2">
              Exchange Bitcoin, Ethereum, and other cryptocurrencies instantly at competitive rates. 
              FINTRAC registered and fully compliant.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-lg transition-all"
                data-testid="crypto-start-trading-btn"
              >
                Start Exchanging
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`tel:${PHONE_NUMBER}`}
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-medium px-6 py-3 sm:px-8 sm:py-4 rounded-lg transition-all"
                data-testid="crypto-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Us
              </a>
            </div>
          </div>
          
          {/* Live Spot Prices Header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6 px-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-xl font-semibold text-white">Live Spot Prices</h2>
              <Info className="w-4 h-4 text-blue-200/50 hidden sm:block" />
            </div>
            <div className="flex items-center gap-2 text-blue-200/60 text-xs sm:text-sm">
              <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${loading ? 'animate-spin' : ''}`} />
              {lastUpdated && <span className="hidden sm:inline">Updated {formatLastUpdated()}</span>}
            </div>
          </div>
          
          {/* Featured Crypto Cards Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6" data-testid="featured-crypto-cards">
            {loading ? (
              <>
                <FeaturedCardSkeleton />
                <FeaturedCardSkeleton />
                <FeaturedCardSkeleton />
                <FeaturedCardSkeleton />
              </>
            ) : (
              featuredCryptos.map((crypto) => (
                <FeaturedCryptoCard key={crypto.id} crypto={crypto} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-6 sm:py-10 px-4 bg-[#001a40]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: Shield, title: "FINTRAC Registered", desc: "Licensed & Regulated" },
              { icon: Lock, title: "100% Secure", desc: "Your funds are safe" },
              { icon: Clock, title: "Instant Exchange", desc: "Same-day transactions" },
              { icon: CheckCircle, title: "Expert Support", desc: "Professional guidance" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#3B82F6]/20 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#3B82F6]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-xs sm:text-sm md:text-base">{item.title}</p>
                  <p className="text-blue-200/50 text-xs hidden sm:block">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Crypto Prices Section */}
      <section className="py-8 sm:py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-t-xl sm:rounded-t-2xl px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-base sm:text-xl font-bold text-gray-900">All Crypto Prices</h2>
              <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-blue-600 font-medium">CAD</span>
              </div>
            </div>
          </div>
          
          {/* Mobile Card List */}
          <div className="md:hidden bg-white rounded-b-xl shadow-lg p-3 space-y-3">
            {loading ? (
              <>
                <MobileCardSkeleton />
                <MobileCardSkeleton />
                <MobileCardSkeleton />
                <MobileCardSkeleton />
                <MobileCardSkeleton />
              </>
            ) : (
              cryptoPrices.map((crypto) => (
                <MobileCryptoCard key={crypto.id} crypto={crypto} />
              ))
            )}
          </div>
          
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-b-2xl overflow-x-auto shadow-xl">
            <table className="w-full" data-testid="crypto-table">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                  <th className="py-3 px-2">Coin</th>
                  <th className="py-3 px-2">Price</th>
                  <th className="py-3 px-2">1h</th>
                  <th className="py-3 px-2">24h</th>
                  <th className="py-3 px-2">7d</th>
                  <th className="py-3 px-2">Volume</th>
                  <th className="py-3 px-2">Market Cap</th>
                  <th className="py-3 px-2">7 Days</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <>
                    <TableRowSkeleton />
                    <TableRowSkeleton />
                    <TableRowSkeleton />
                    <TableRowSkeleton />
                    <TableRowSkeleton />
                  </>
                ) : (
                  cryptoPrices.map((crypto) => (
                    <CryptoTableRow key={crypto.id} crypto={crypto} />
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Disclaimer */}
          <div className="mt-4 p-3 sm:p-4 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-600 text-xs sm:text-sm">
              <strong className="text-gray-900">Note:</strong> Live spot rates updated every 60 seconds. 
              Actual rates at FonderX may differ. Contact us for large transactions.
            </p>
          </div>
          
          {/* Crypto News Section - 4 Cards */}
          <div className="mt-8 sm:mt-10">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                  <Newspaper className="w-5 h-5 text-[#3B82F6]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Crypto News</h3>
              </div>
              <Link 
                to="/crypto/news" 
                className="flex items-center gap-2 text-[#3B82F6] hover:text-[#2563EB] font-medium text-sm transition-colors"
                data-testid="read-more-news-btn"
              >
                Read More
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {loadingNews ? (
                [...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse border border-gray-100">
                    <div className="h-32 sm:h-36 bg-gray-200" />
                    <div className="p-3 sm:p-4 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-full" />
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-100 rounded w-1/3" />
                    </div>
                  </div>
                ))
              ) : news.length > 0 ? (
                news.map((article, idx) => (
                  <a
                    key={idx}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all group"
                    data-testid={`news-card-${idx}`}
                  >
                    <div className="h-32 sm:h-36 overflow-hidden bg-gray-100">
                      <img
                        src={article.image || "https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=400"}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=400"; }}
                      />
                    </div>
                    <div className="p-3 sm:p-4">
                      <p className="text-gray-900 font-medium text-sm line-clamp-2 group-hover:text-[#3B82F6] transition-colors mb-2">
                        {article.title}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">{article.source}</span>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#3B82F6]" />
                      </div>
                    </div>
                  </a>
                ))
              ) : (
                <div className="col-span-4 text-center py-8 bg-white rounded-xl border border-gray-100">
                  <Newspaper className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No news available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What is Spot Rate Section */}
      <section className="py-10 sm:py-16 px-4 bg-[#001435]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
                What is a Spot Rate?
              </h2>
              <p className="text-blue-200/70 text-sm sm:text-base mb-4 sm:mb-6">
                A <strong className="text-white">spot rate</strong> is the current market price for immediate settlement. 
                Unlike futures, spot trades settle "on the spot" - you receive your crypto or cash right away.
              </p>
              <div className="space-y-3 sm:space-y-4">
                {[
                  "Real-time pricing from global markets",
                  "Updated every 60 seconds",
                  "Transparent and verifiable rates",
                  "Best for immediate transactions"
                ].map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                    <span className="text-blue-100 text-sm sm:text-base">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-lg">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Why Exchange With FonderX?</h3>
              <div className="space-y-4 sm:space-y-6">
                {[
                  { icon: TrendingUp, title: "Competitive Rates", desc: "Minimal spreads for large transactions" },
                  { icon: Clock, title: "Instant Settlement", desc: "Walk in with cash, walk out with crypto" },
                  { icon: Shield, title: "Fully Regulated", desc: "FINTRAC registered MSB" },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 sm:gap-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#001435] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#3B82F6]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{item.title}</h4>
                      <p className="text-gray-600 text-xs sm:text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 sm:py-16 px-4 bg-gradient-to-r from-[#001435] to-[#003080]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Ready to Exchange Cryptocurrency?
          </h2>
          <p className="text-blue-200/70 text-sm sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
            Visit our New Westminster office or call us to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-lg transition-all"
              data-testid="crypto-cta-visit-btn"
            >
              Visit Our Office
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#001435] font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-lg transition-all"
              data-testid="crypto-cta-call-btn"
            >
              <Phone className="w-5 h-5" />
              +1 (778) 759-0400
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
